import { useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

import classes from "./CartQuickActionsButton.module.less";
import { Button } from "~/common/components/Button/Button";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { MENU_POS_RIGHT } from "~/common/components/Menu/MenuPositions";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { canSeeWishlistButton } from "~/utils/features";
import { Permission, userHasPermission } from "~/utils/permissions";
import { ReportType } from "./DownloadReportButton/api/downloadCartReport";
import { EnterDesignationBtn } from "./EnterDesignationButton";
import BulkATCButton from "./BulkATCButton";
import AddCartToWishlistBtn from "./AddCartToWishlistBtn";
import ClearCartButton from "./ClearCartButton";
import { DownloadReportButton } from "./DownloadReportButton/DownloadReportButton";
import { LoadAllPnAButton } from "./LoadAllPnAButton";
import { SwitchProductVersions } from "./SwitchProductVersions";
import { ChoosePriceReferenceForCart } from "./ChoosePriceReferenceForCart";

interface Props {
  disableReportDownload: boolean;
  switchProductVersionForCart: () => void;
  updatePriceReferenceForCart: (priceReference: string) => void;
}

export const CartQuickActionsButton = ({
  disableReportDownload,
  switchProductVersionForCart,
  updatePriceReferenceForCart,
}: Props) => {
  const { t } = useTranslation();
  const canBulkUpload = userHasPermission(Permission.BULK_UPLOAD);

  const { val: showActionBtnMenu, setFalse: closeActionBtnMenu, toggle: openActionBtnMenu } = useBooleanState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenActionBtnMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    openActionBtnMenu();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActionBtnMenu = () => {
    closeActionBtnMenu();
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        color="secondary"
        onClick={handleOpenActionBtnMenu}
        data-test-id={"btn-actions"}
        endIcon={<ExpandMoreIcon sx={{ marginLeft: "8px", transform: showActionBtnMenu ? "rotate(180deg)" : "" }} />}
      >
        {t("cartPage.btn.actions")}
      </Button>
      <Menu
        keepMounted
        open={showActionBtnMenu}
        anchorEl={anchorEl}
        onClose={handleCloseActionBtnMenu}
        onClick={handleCloseActionBtnMenu}
        {...MENU_POS_RIGHT}
        sx={{ marginTop: "10px" }}
        slotProps={{ paper: { sx: { borderRadius: "12px", border: `1px solid ${C_OUTLINE_VARIANT}` } } }}
      >
        <MenuItem className={classes.menuItem}>
          <EnterDesignationBtn className={classes.actionBtn} />
        </MenuItem>
        {canBulkUpload && (
          <MenuItem className={classes.menuItem}>
            <BulkATCButton isB2BCart={true} className={classes.actionBtn} />
          </MenuItem>
        )}
        {canSeeWishlistButton() && (
          <MenuItem className={classes.menuItem}>
            <AddCartToWishlistBtn isB2BCart={true} className={classes.actionBtn} />
          </MenuItem>
        )}
        <MenuItem className={classes.menuItem}>
          <ClearCartButton isB2BCart={true} className={classes.actionBtn} />
        </MenuItem>
        <MenuItem className={classes.menuItem}>
          <DownloadReportButton
            isB2BCart={true}
            type={ReportType.XLSX}
            className={classes.actionBtn}
            disabled={disableReportDownload}
          />
        </MenuItem>
        <LoadAllPnAButton className={classes.actionBtn} menuClassName={classes.menuItem} />
        <SwitchProductVersions
          isB2BCart={true}
          className={classes.actionBtn}
          menuClassName={classes.menuItem}
          switchProductVersion={switchProductVersionForCart}
        />
        <ChoosePriceReferenceForCart
          isB2BCart={true}
          className={classes.actionBtn}
          menuClassName={classes.menuItem}
          updatePriceReference={updatePriceReferenceForCart}
        />
      </Menu>
    </>
  );
};
